import { useState } from 'react';
import { Badge, Group, Progress, SimpleGrid, Stack, Table, Text, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@prooffleet/client';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface Slot {
  slotKey: string;
  unitId: string;
  unitNumber: string;
  templateKey: string;
  dueAt: string;
  windowEndsAt: string;
  status: 'fulfilled' | 'missed' | 'pending';
  inspectionId: string | null;
  missReason: string | null;
}

function isoDay(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 86_400_000).toISOString().slice(0, 10);
}

const STATUS_COLOR: Record<Slot['status'], string> = {
  fulfilled: 'gray',
  missed: 'red',
  pending: 'orange',
};

/**
 * Картина соблюдения расписания.
 *
 * Ожидаемые слоты не хранятся — они выводятся из расписания и сопоставляются
 * с фактическими осмотрами при каждом запросе. Поэтому правка расписания
 * меняет картину сразу, без починки уже созданных записей.
 *
 * Показатель считается по закрытым окнам: незакрытое окно ещё не пропуск,
 * и включать его в знаменатель значило бы занижать цифру каждое утро.
 */
export function Compliance(): React.JSX.Element {
  const [from, setFrom] = useState(isoDay(-14));
  const [to, setTo] = useState(isoDay(1));

  const { data, isLoading } = useQuery({
    queryKey: ['compliance', from, to],
    queryFn: () =>
      api<{ slots: Slot[] }>(
        `/compliance?from=${from}T00:00:00Z&to=${to}T00:00:00Z`,
      ),
  });

  const slots = data?.slots ?? [];
  const settled = slots.filter((s) => s.status !== 'pending');
  const fulfilled = settled.filter((s) => s.status === 'fulfilled').length;
  const missed = settled.filter((s) => s.status === 'missed').length;
  const unexplained = slots.filter((s) => s.status === 'missed' && s.missReason === null).length;
  const rate = settled.length === 0 ? null : Math.round((fulfilled / settled.length) * 100);

  return (
    <Stack gap={0}>
      <PageHeader
        title="Compliance"
        hint="Expected inspections against what actually arrived. Windows still open are not counted either way."
        action={
          <Group gap="xs">
            <TextInput size="xs" type="date" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
            <TextInput size="xs" type="date" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
          </Group>
        }
      />

      {/* Четыре нуля над пустым экраном — шум: сначала нужно расписание. */}
      {slots.length > 0 && (
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
        {[
          { label: 'On time', value: rate === null ? '—' : `${String(rate)}%`, tone: undefined },
          { label: 'Fulfilled', value: String(fulfilled), tone: undefined },
          { label: 'Missed', value: String(missed), tone: missed > 0 ? 'red.8' : undefined },
          {
            label: 'Unexplained',
            value: String(unexplained),
            tone: unexplained > 0 ? 'orange.8' : undefined,
          },
        ].map((card) => (
          <div key={card.label}>
            <Text className="pf-eyebrow">{card.label}</Text>
            <Text
              className="pf-num"
              fz={26}
              fw={620}
              lh={1.1}
              {...(card.tone === undefined ? {} : { c: card.tone })}
            >
              {card.value}
            </Text>
          </div>
        ))}
      </SimpleGrid>
      )}

      {rate !== null && (
        <Progress.Root size="sm" mb="lg">
          <Progress.Section value={(fulfilled / settled.length) * 100} color="gray.5" />
          <Progress.Section value={(missed / settled.length) * 100} color="red.7" />
        </Progress.Root>
      )}

      {isLoading && <TableSkeleton rows={6} cols={5} />}

      {!isLoading && slots.length === 0 && (
        <EmptyState
          title="Nothing expected in this period"
          hint="Add a schedule to start measuring compliance. Without one, nothing can be missed."
        />
      )}

      {!isLoading && slots.length > 0 && (
        <Table.ScrollContainer minWidth={780}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="18%">Unit</Table.Th>
                <Table.Th w="18%">Template</Table.Th>
                <Table.Th w="22%">Due</Table.Th>
                <Table.Th w="16%">Status</Table.Th>
                <Table.Th w="26%">Reason</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {slots.map((slot) => (
                <Table.Tr key={slot.slotKey}>
                  <Table.Td>
                    <Link
                      to={`/units/${slot.unitId}`}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <Text fw={580} fz="sm">
                        {slot.unitNumber}
                      </Text>
                    </Link>
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-mono">{slot.templateKey}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-mono">{new Date(slot.dueAt).toLocaleString()}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={STATUS_COLOR[slot.status]}>
                      {slot.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {slot.status !== 'missed' ? (
                      <Text fz="sm" c="dimmed">
                        —
                      </Text>
                    ) : slot.missReason === null ? (
                      <Text fz="sm" c="orange.8">
                        unexplained
                      </Text>
                    ) : (
                      <Text fz="sm" c="gray.7">
                        {slot.missReason}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
